/**
 * stageMotion — how a first-run step arrives and leaves.
 *
 * Every animated element carries `data-a` with its kind: orb, chip
 * (a row of chips), cap (the capsule), block, tile, text, cta.
 *
 *   enterStage(root, dir)      — the orb breathes in, one ring pulse
 *                                and colour wash fire, chips fly in
 *                                from the side with a blur/saturate
 *                                trail, the capsule blooms, text rises.
 *   swapStage(root, dir, apply) — leaving forward: every chip gathers
 *                                into the lit capsule, the capsule
 *                                flares and collapses, the rest blurs
 *                                out; then `apply()` changes the step
 *                                and enterStage runs on the new one.
 */

const reduced = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

const EASE_IN = 'cubic-bezier(.16,.84,.24,1)';

function clearAnimations(node: Element) {
  node.getAnimations().forEach((a) => {
    if (typeof CSSAnimation !== 'undefined' && a instanceof CSSAnimation) return;
    a.cancel();
  });
}

export function enterStage(root: HTMLElement | null, dir: 1 | -1 = 1) {
  if (!root || typeof root.animate !== 'function') return;
  const reduce = reduced();
  requestAnimationFrame(() => {
    if (!reduce) {
      root.querySelectorAll<HTMLElement>('[data-wash]').forEach((el) =>
        el.animate(
          [{ opacity: 0, transform: 'scale(.6)' }, { opacity: 0.55, transform: 'scale(1)', offset: 0.3 }, { opacity: 0, transform: 'scale(1.5)' }],
          { duration: 1800, easing: 'ease-out', fill: 'backwards' },
        ),
      );
      root.querySelectorAll<HTMLElement>('[data-ring]').forEach((el, i) =>
        el.animate(
          [{ opacity: 1, transform: 'scale(.4)' }, { opacity: 0.9, transform: 'scale(.9)', offset: 0.35 }, { opacity: 0, transform: 'scale(1.55)' }],
          { duration: 2200, delay: 300 + i * 420, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards' },
        ),
      );
    }
    let si = 0;
    let ti = 0;
    root.querySelectorAll<HTMLElement>('[data-a]').forEach((el) => {
      clearAnimations(el);
      el.querySelectorAll('button').forEach(clearAnimations);
      if (reduce) return;
      const k = el.dataset.a;
      let kf: Keyframe[];
      let d: number;
      let dl: number;
      if (k === 'orb') {
        kf = [{ opacity: 0.2, transform: 'scale(.7)' }, { opacity: 1, transform: 'none' }];
        d = 1800; dl = 0;
      } else if (k === 'chip') {
        kf = [
          { opacity: 0, transform: `translate(${dir * 60}px,18px) scale(.84) rotate(${dir * 4}deg)`, filter: 'blur(10px) saturate(3) hue-rotate(40deg)' },
          { opacity: 1, transform: 'none', filter: 'blur(0px) saturate(1) hue-rotate(0deg)' },
        ];
        d = 1100; dl = 200 + si++ * 130;
      } else if (k === 'cap') {
        kf = [
          { opacity: 0, transform: 'scale(.8)', filter: 'blur(14px)' },
          { opacity: 1, transform: 'scale(1.035)', filter: 'blur(0px)', offset: 0.62 },
          { opacity: 1, transform: 'none', filter: 'blur(0px)' },
        ];
        d = 1200; dl = 240 + si++ * 130;
      } else if (k === 'block') {
        kf = [{ opacity: 0, transform: 'translateY(26px) scale(.94)', filter: 'blur(8px)' }, { opacity: 1, transform: 'none', filter: 'blur(0px)' }];
        d = 1000; dl = 220 + si++ * 130;
      } else if (k === 'tile') {
        kf = [{ opacity: 0, transform: 'scale(.94) translateY(24px)' }, { opacity: 1, transform: 'none' }];
        d = 1400; dl = (si % 5) * 90;
      } else if (k === 'text') {
        kf = [{ opacity: 0, transform: 'translateY(22px)', filter: 'blur(6px)' }, { opacity: 1, transform: 'none', filter: 'blur(0px)' }];
        d = 1000; dl = 420 + si * 70 + ti++ * 140;
      } else {
        kf = [{ opacity: 0, transform: 'translateY(30px) scale(.96)' }, { opacity: 1, transform: 'none' }];
        d = 1000; dl = 760 + si * 70;
      }
      el.animate(kf, { duration: d, delay: dl, easing: EASE_IN, fill: 'backwards' });
    });
  });
}

let busy = false;

export function swapStage(root: HTMLElement | null, dir: 1 | -1, apply: () => void) {
  if (busy) return;
  if (!root || typeof root.animate !== 'function' || reduced()) {
    apply();
    setTimeout(() => enterStage(root, dir), 40);
    return;
  }
  busy = true;
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    busy = false;
    apply();
    setTimeout(() => enterStage(root, dir), 40);
  };
  const els = [...root.querySelectorAll<HTMLElement>('[data-a]')];
  const capEl = dir > 0 ? root.querySelector<HTMLElement>('[data-a="cap"]') : null;
  const t = capEl ? capEl.getBoundingClientRect() : null;
  const anims: Animation[] = [];
  let n = 0;
  els.forEach((el) => {
    const k = el.dataset.a;
    if (k === 'chip' && t) {
      el.querySelectorAll<HTMLElement>('button').forEach((b) => {
        const r = b.getBoundingClientRect();
        const dx = t.left + t.width / 2 - (r.left + r.width / 2);
        const dy = t.top + t.height / 2 - (r.top + r.height / 2);
        anims.push(
          b.animate(
            [
              { transform: 'none', opacity: 1, filter: 'blur(0px)' },
              { transform: `translate(${dx * 0.55}px,${dy * 0.55}px) scale(.85)`, opacity: 1, offset: 0.55 },
              { transform: `translate(${dx}px,${dy}px) scale(.2)`, opacity: 0, filter: 'blur(4px)' },
            ],
            { duration: 900, delay: n++ * 110, easing: 'cubic-bezier(.55,0,.6,1)', fill: 'forwards' },
          ),
        );
      });
      return;
    }
    if (k === 'orb') {
      anims.push(
        el.animate(
          [{ opacity: 1, transform: 'none' }, { opacity: 1, transform: 'scale(.9)', offset: 0.55 }, { opacity: 0.3, transform: 'scale(1.18)' }],
          { duration: 1500, easing: 'cubic-bezier(.4,0,.6,1)', fill: 'forwards' },
        ),
      );
      return;
    }
    if (el === capEl) return;
    anims.push(
      el.animate(
        [
          { opacity: 1, transform: 'none', filter: 'blur(0px)' },
          { opacity: 0, transform: `translate(${-dir * 40}px,-10px) scale(.95)`, filter: 'blur(8px)' },
        ],
        { duration: 480, delay: 60, easing: 'cubic-bezier(.4,0,.9,.6)', fill: 'forwards' },
      ),
    );
  });
  if (capEl) {
    anims.push(
      capEl.animate(
        [
          { opacity: 1, transform: 'none', filter: 'brightness(1) blur(0px)' },
          { opacity: 1, transform: 'scale(1.06)', filter: 'brightness(1.7) blur(0px)', offset: 0.45 },
          { opacity: 0, transform: 'scale(.6)', filter: 'brightness(2) blur(10px)' },
        ],
        { duration: 820, delay: Math.max(0, n - 1) * 110 + 620, easing: 'cubic-bezier(.5,0,.5,1)', fill: 'forwards' },
      ),
    );
  }
  Promise.all(anims.map((a) => a.finished.catch(() => undefined))).then(finish);
  setTimeout(finish, 4000);
}

/**
 * A chip flying into the capsule on its own (the "Try it" prompt on
 * Meet Nova). Resolves when it lands.
 */
export function flyChipToCapsule(root: HTMLElement | null, chip: HTMLElement | null): Promise<void> {
  const cap = root?.querySelector<HTMLElement>('[data-a="cap"]');
  if (!root || !chip || !cap || typeof chip.animate !== 'function' || reduced()) return Promise.resolve();
  const r = chip.getBoundingClientRect();
  const t = cap.getBoundingClientRect();
  const dx = t.left + t.width / 2 - (r.left + r.width / 2);
  const dy = t.top + t.height / 2 - (r.top + r.height / 2);
  const an = chip.animate(
    [
      { transform: 'none', opacity: 1 },
      { transform: `translate(${dx * 0.6}px,${dy * 0.6}px) scale(.85)`, opacity: 1, offset: 0.6 },
      { transform: `translate(${dx}px,${dy}px) scale(.25)`, opacity: 0 },
    ],
    { duration: 580, easing: 'cubic-bezier(.5,0,.6,1)', fill: 'forwards' },
  );
  cap.animate(
    [{ transform: 'none', filter: 'brightness(1)' }, { transform: 'scale(1.05)', filter: 'brightness(1.6)' }, { transform: 'none', filter: 'brightness(1)' }],
    { duration: 560, delay: 460, easing: 'ease-out' },
  );
  return new Promise((resolve) =>
    setTimeout(() => {
      resolve();
      requestAnimationFrame(() => an.cancel());
    }, 580),
  );
}
